// i18n pilot infra (Stage 1, 2026-09-23). ICU via i18next-icu, react-i18next.
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';

export const NAMESPACES = ['common', 'auth', 'messages'] as const;
export type Namespace = (typeof NAMESPACES)[number];

const FALLBACK_LNG: Record<string, readonly string[]> = {
  'es-VE': ['es-MX'],
  'es-CO': ['es-MX'],
  'es-AR': ['es-MX'],
  'es-ES': ['es-MX'],
  'es': ['es-MX'],
  default: ['es-MX'],
};

let initialized = false;
function baseInit(): void {
  if (initialized) return;
  i18next.use(initReactI18next).use(ICU).init({
    lng: 'es-MX',
    fallbackLng: FALLBACK_LNG,
    ns: [...NAMESPACES],
    defaultNS: 'common',
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    parseMissingKeyHandler: (key: string) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing key: ${key}`);
      }
      // (2026-09-24) Never leak the missing-key warning glyph into prod UI
      // (F-16: sidebar group heading rendered "⚠Finanzas"). The dev console
      // warn above is the indicator; prod renders the raw key.
      return import.meta.env.DEV ? `⚠${key}` : key;
    },
  });
  initialized = true;
}

// Vite dynamic-import map over all locale json files (lazy).
const localeFiles = import.meta.glob<{ default: Record<string, unknown> }>(
  '../locales/*/*.json',
);

/** Locales whose bundles are needed to render `locale` (primary first). */
export function localesFor(locale: string): string[] {
  if (locale === 'en') return ['en'];
  if (locale === 'es-MX') return ['es-MX'];
  if (locale.startsWith('es')) return [locale, 'es-MX'];
  return ['es-MX'];
}

/** Dynamic-import ../locales/<needed>/<ns>.json for namespaces common/auth/messages. */
export async function ensureLanguage(locale: string): Promise<string[]> {
  baseInit();
  const targets = localesFor(locale);
  await Promise.all(
    targets.flatMap((lng) =>
      NAMESPACES.map(async (ns) => {
        const loader = localeFiles[`../locales/${lng}/${ns}.json`];
        if (!loader) return;
        const data = (await loader()).default;
        if (!i18next.hasResourceBundle(lng, ns)) {
          i18next.addResourceBundle(lng, ns, data, true, true);
        }
      }),
    ),
  );
  return targets;
}

/** ensureLanguage + i18next.changeLanguage; returns the resolved locale. */
export async function setLanguage(locale: string): Promise<string> {
  const targets = await ensureLanguage(locale);
  const resolved = targets[0];
  await i18next.changeLanguage(resolved);
  return resolved;
}

/** localStorage 'v-lang' || ('en' if navigator.language starts with 'en') || 'es-MX'. */
export function getInitialLocale(): string {
  try {
    const stored = localStorage.getItem('v-lang');
    if (stored) return stored;
  } catch {
    /* SSR/private-mode: ignore */
  }
  if (typeof navigator !== 'undefined' && navigator.language?.startsWith('en')) {
    return 'en';
  }
  return 'es-MX';
}
